import { useEffect, useState } from "react"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { describeRecentChange, recentChangeBadge } from "../budget-utils"
import type { RecommendationRow } from "../types"

interface RecentChangesAckDialogProps {
  isOpen: boolean
  /** The SELECTED rows that carry a recent-change label. */
  rows: RecommendationRow[]
  /** The recent change window (Financial Settings), when known. */
  windowDays: number | null
  onCancel: () => void
  /** The user acknowledged the labels; the review may open. */
  onConfirm: () => void
}

/**
 * The gate between "Review Changes" and the review itself when any selected
 * row had a recent manual change or its first spend inside the recent change
 * window. A recent change is a heads-up, not a lock: the user can include
 * the row, but must say so explicitly — the Tue/Fri run once undid a $200
 * budget a person had set two days earlier without anyone noticing.
 */
export default function RecentChangesAckDialog({
  isOpen,
  rows,
  windowDays,
  onCancel,
  onConfirm,
}: RecentChangesAckDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false)
  // A fresh open is a fresh decision.
  useEffect(() => {
    if (isOpen) setAcknowledged(false)
  }, [isOpen])

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onCancel}
      width={640}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-xl p-0 text-left max-h-[85vh] flex flex-col",
      }}
    >
      <div className="px-6 py-4 border-b border-basic-blue flex items-center gap-2">
        <ExclamationTriangleIcon className="w-5 h-5 text-amber-300 shrink-0" />
        <h2 className="text-lg font-semibold text-white">
          {rows.length === 1
            ? "1 selected row had a recent change"
            : `${rows.length} selected rows had recent changes`}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <p className="text-sm text-gray-300">
          These campaigns / ad sets had their budget or bid target changed, were
          enabled, or started spending within the last {windowDays ?? 7} days.
          Their recent performance may not yet reflect that change. They will be
          updated like every other selected row once you continue.
        </p>

        <ul className="divide-y divide-basic-blue border border-basic-blue rounded-lg">
          {rows.map(row => (
            <li key={row.entityId} className="px-3 py-2 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="text-white truncate min-w-0"
                  title={row.entityName}
                >
                  {row.entityName}
                </span>
                <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-basic-black rounded shrink-0">
                  {row.entityType}
                </span>
                <span className="text-xs text-gray-400 shrink-0">
                  {row.channel}
                </span>
                {row.recentChanges?.map(change => (
                  <span
                    key={`${change.reason}-${change.at}`}
                    className={cn(
                      "text-xs px-1.5 py-0.5 border rounded shrink-0",
                      change.reason === "first-spend"
                        ? "text-sky-300 bg-sky-900/40 border-sky-700"
                        : "text-amber-300 bg-amber-900/40 border-amber-700",
                    )}
                  >
                    {recentChangeBadge(change)}
                  </span>
                ))}
              </div>
              <ul className="mt-1 text-xs text-gray-400 space-y-0.5">
                {row.recentChanges?.map(change => (
                  <li key={`${change.reason}-${change.at}-desc`}>
                    {describeRecentChange(change)}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        <label className="flex items-start gap-2 text-sm text-amber-200 cursor-pointer">
          <Checkbox
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
            className="checkbox-sm mt-0.5"
          />
          <span>
            I understand these were changed recently and want to include them in
            this budget update. (Uncheck a row in the table to leave it as is.)
          </span>
        </label>
      </div>

      <div className="px-6 py-4 border-t border-basic-blue flex justify-end gap-3">
        <Button variant={{ variant: "outlined" }} size="sm" onClick={onCancel}>
          Back
        </Button>
        <Button
          variant={{ variant: "solid", color: "blue" }}
          size="sm"
          onClick={onConfirm}
          disabled={!acknowledged}
        >
          Continue to review
        </Button>
      </div>
    </Dialog>
  )
}
